import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { TABLES } from '../lib/tables';
import { downloadCsv } from '../lib/statistics';
import { buildContext, computeCoverage } from '../lib/suggestions-engine';

const SummaryCard = ({ label, value, suffix }) => (
  <Card sx={{ borderRadius: 3, flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
    <CardContent sx={{ textAlign: 'center' }}>
      <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '1.4rem', sm: '1.8rem' } }}>
        {value}{suffix || ''}
      </Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </CardContent>
  </Card>
);

export default function StatisticsSuggestions() {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [context, setContext] = useState(null);
  const [names, setNames] = useState({});

  const loadData = async () => {
    setLoading(true);
    const [auditRes, studentsRes, requestsRes, activitiesRes, modulesRes, typesRes] = await Promise.all([
      supabase
        .from(TABLES.audit_log)
        .select('id, created_at, entity_type, entity_id, entity_name, action_description, new_values')
        .in('entity_type', ['suggestion_shown', 'suggestion_clicked'])
        .order('created_at', { ascending: false })
        .limit(500),
      supabase.from(TABLES.profiles).select('id, nume, prenume, clasa, specializare').eq('rol', 'elev'),
      supabase
        .from(TABLES.credit_requests)
        .select('id, id_elev, id_tip_credit, id_module, id_activitate_predefinita, nume_activitate, credite_solicitate, credite_acordate, status')
        .eq('status', 'validat'),
      supabase.from(TABLES.predefined_activities).select('id, id_tip_credit, id_module, nume_activitate, credite_per_ora, ore_recomandate, credite_acordate').eq('este_activ', true),
      supabase.from(TABLES.modules).select('id, cod_module, nume_module, credite_tinta, ordine').order('ordine'),
      supabase.from(TABLES.credit_types).select('id, nume_tip, categorie'),
    ]);

    const auditRows = auditRes.data || [];
    const students = studentsRes.data || [];
    setNames(Object.fromEntries(students.map((s) => [s.id, `${s.nume} ${s.prenume}`])));
    setEvents(auditRows);
    setContext(
      buildContext({
        students,
        requests: requestsRes.data || [],
        activities: activitiesRes.data || [],
        modules: modulesRes.data || [],
        creditTypes: typesRes.data || [],
      }),
    );
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const metrics = useMemo(() => {
    if (!context) return null;
    const shown = events.filter((event) => event.entity_type === 'suggestion_shown');
    const clicked = events.filter((event) => event.entity_type === 'suggestion_clicked');

    const accepted = clicked.filter((event) => {
      const studentId = event.new_values?.id_elev;
      const activityName = event.new_values?.nume_activitate || event.entity_name;
      if (!studentId || !activityName) return false;
      return context.validated.some(
        (request) => request.id_elev === studentId && request.nume_activitate === activityName,
      );
    }).length;

    return {
      shownCount: shown.length,
      clickedCount: clicked.length,
      precision: clicked.length > 0 ? (accepted / clicked.length) * 100 : null,
      diversity: new Set(shown.map((event) => event.new_values?.modul).filter(Boolean)).size,
      coverage: computeCoverage(context),
    };
  }, [context, events]);

  const handleExport = () => {
    downloadCsv(
      'metrici_sugestii.csv',
      ['Data', 'Tip', 'Elev', 'Sugestie', 'Modul'],
      events.map((event) => [
        event.created_at,
        event.entity_type,
        names[event.new_values?.id_elev] || '-',
        event.entity_name,
        event.new_values?.modul || '-',
      ]),
    );
  };

  if (loading || !metrics) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Metrici sugestii</Typography>
          <Typography variant="body2" color="text.secondary">
            Indicatori pentru evaluarea sistemului de recomandări
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData}>Reîncarcă</Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>Export CSV</Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <SummaryCard
          label="Precizie (click și cerere trimisă)"
          value={metrics.precision === null ? '-' : metrics.precision.toFixed(0)}
          suffix={metrics.precision === null ? '' : '%'}
        />
        <SummaryCard label="Sugestii afișate" value={metrics.shownCount} />
        <SummaryCard label="Sugestii accesate" value={metrics.clickedCount} />
        <SummaryCard label="Diversitate (module acoperite)" value={metrics.diversity} />
      </Box>

      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold">
            Acoperire: {metrics.coverage.toFixed(0)}% dintre elevi primesc cel puțin 3 sugestii
          </Typography>
        </CardContent>
      </Card>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Tip</TableCell>
              <TableCell>Elev</TableCell>
              <TableCell>Sugestie</TableCell>
              <TableCell>Modul</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.slice(0, 200).map((event) => (
              <TableRow key={`${event.entity_type}-${event.id}`} hover>
                <TableCell>
                  {event.created_at ? new Date(event.created_at).toLocaleString('ro-RO') : '-'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={event.entity_type === 'suggestion_clicked' ? 'accesată' : 'afișată'}
                    color={event.entity_type === 'suggestion_clicked' ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell>{names[event.new_values?.id_elev] || '-'}</TableCell>
                <TableCell>{event.entity_name}</TableCell>
                <TableCell>{event.new_values?.modul || '-'}</TableCell>
              </TableRow>
            ))}
            {events.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Nu există evenimente de sugestii încă.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}
